/**
 * 5-tab Configure panel for an existing SubContractorHub integration.
 *
 * Story SCH-2.1 owns this component. Tabs (per architecture line 990 + epic
 * lines 596-598):
 *   - Connection       (this story — read-only summary + Rotate/Reconnect CTAs)
 *   - Field Mapping    (this story — full implementation)
 *   - Stage Mapping    (placeholder; SCH-2.2)
 *   - Adder Mapping    (placeholder; SCH-2.3)
 *   - Document Filters (placeholder; SCH-2.4)
 *
 * The matching no-row first-time Connect panel is `ConnectSubContractorHubPanel`.
 *
 * Pending-state blocker (AC11): the panel maintains a single `isBlocking`
 * flag set by FieldMappingTab while its mutations are in flight. The
 * panel's onClose wrapper no-ops while blocked so escape/backdrop close
 * paths can't interrupt an in-flight mutation. The locked-in pattern
 * from SCH-1.4 / 1.5 / 1.6 — Fly.io cold-starts can take 2-10s and a
 * freeze-looking UI would be confusing.
 */

import { Eye, PlayCircle } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { SlidePanel } from '@/components/ui/slide-panel';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { UnsavedChangesDialog } from '@/components/ui/unsaved-changes-dialog';
import {
  useActiveBackfill,
  useCanRunBackfill,
  useLatestBackfillRun,
} from '@/hooks/integrations/useBackfillProgress';
import type { ActiveBackfillResponse } from '@/types/integrations/backfillProgress';
import type { SubContractorHubIntegration } from '@/types/integrations/subcontractorhub';
import { AdderMappingTab } from './AdderMappingTab';
import { BackfillPreviewModal } from './BackfillPreviewModal';
import { ConfigureConnectionTab } from './ConfigureConnectionTab';
import { DocumentFiltersTab } from './DocumentFiltersTab';
import { InsightsTab } from './InsightsTab';
import { MissingProjectsView } from './MissingProjectsView';
import { SyncErrorsView } from './SyncErrorsView';
import { SyncHistoryTab } from './SyncHistoryTab';
import { FieldMappingTab } from './FieldMappingTab';
import { StageMappingTab } from './StageMappingTab';

type DirtyTabKey = 'document-filters';

interface SubContractorHubPanelProps {
  isOpen: boolean;
  onClose: () => void;
  integration: SubContractorHubIntegration;
  onRequestRotateKey: () => void;
  onRequestReconnect: () => void;
}

export function SubContractorHubPanel({
  isOpen,
  onClose,
  integration,
  onRequestRotateKey,
  onRequestReconnect,
}: SubContractorHubPanelProps) {
  const [isBlocking, setIsBlocking] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [resumeSyncRunId, setResumeSyncRunId] = useState<string | null>(null);
  // SCH-3.5: when set, modal opens read-only on a previously-finished run.
  const [viewLastRunSyncId, setViewLastRunSyncId] = useState<string | null>(null);
  const [dirtyTabs, setDirtyTabs] = useState<Record<DirtyTabKey, boolean>>({
    'document-filters': false,
  });
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  // Active backfill detection — surfaces the Resume banner when a prior run
  // hasn't finished. Only polled while the panel is open.
  const { data: activeBackfillResult } = useActiveBackfill({ enabled: isOpen });
  const activeBackfill = activeBackfillResult?.data ?? null;
  const { canRun: canRunBackfill, loading: roleLoading } = useCanRunBackfill();

  // SCH-3.5: most-recent run id for the "View last run" button. Only shown
  // when there's NO active backfill (active path is covered by the Resume
  // banner). Queried lazily while the panel is open.
  const { data: latestSyncRunId } = useLatestBackfillRun(integration.id, {
    enabled: isOpen && !activeBackfill,
  });

  const isAnyTabDirty = useMemo(
    () => Object.values(dirtyTabs).some(Boolean),
    [dirtyTabs],
  );

  const setDirtyForTab = useCallback(
    (tabKey: DirtyTabKey) => (dirty: boolean) =>
      setDirtyTabs((prev) =>
        prev[tabKey] === dirty ? prev : { ...prev, [tabKey]: dirty },
      ),
    [],
  );

  // Pre-animation veto: if a mutation is in flight OR any tab has unsaved
  // changes, abort the close. For unsaved changes, show the discard dialog
  // and let the user decide. Returning false from this hook keeps the panel
  // visible; the dialog overlays it instead of replacing it.
  const handleBeforeClose = useCallback((): boolean => {
    if (isBlocking) return false;
    if (isAnyTabDirty) {
      setShowDiscardDialog(true);
      return false;
    }
    return true;
  }, [isBlocking, isAnyTabDirty]);

  const handleDiscardAndClose = useCallback(() => {
    setShowDiscardDialog(false);
    // Clear dirty flags so the next close attempt isn't gated again.
    setDirtyTabs({ 'document-filters': false });
    onClose();
  }, [onClose]);

  return (
    <>
      <SlidePanel
        isOpen={isOpen}
        onClose={onClose}
        onBeforeClose={handleBeforeClose}
        title="Configure SubContractorHub integration"
        width="2xl"
        topOffset="4.5rem"
      >
        <div className="flex flex-col gap-4">
          {activeBackfill && (
            <_ResumeBanner
              backfill={activeBackfill}
              canRun={canRunBackfill}
              roleLoading={roleLoading}
              onResume={() => {
                setResumeSyncRunId(activeBackfill.syncRunId);
                setIsPreviewOpen(true);
              }}
            />
          )}

          <Tabs defaultValue="connection" className="w-full">
            <TabsList className="grid h-auto w-full grid-cols-2 gap-1 sm:grid-cols-4">
              <TabsTrigger value="connection">Connection</TabsTrigger>
              <TabsTrigger value="field-mapping">Field Mapping</TabsTrigger>
              <TabsTrigger value="stage-mapping">Stage Mapping</TabsTrigger>
              <TabsTrigger value="adder-mapping">Adder Mapping</TabsTrigger>
              <TabsTrigger value="document-filters">Document Filters</TabsTrigger>
              <TabsTrigger value="sync-history">Sync History</TabsTrigger>
              <TabsTrigger value="insights">Insights</TabsTrigger>
              <TabsTrigger value="sync-errors">Sync Errors</TabsTrigger>
              <TabsTrigger value="missing-projects">Missing Projects</TabsTrigger>
            </TabsList>

            <TabsContent value="connection" className="mt-4">
              <ConfigureConnectionTab
                integration={integration}
                onRequestRotateKey={onRequestRotateKey}
                onRequestReconnect={onRequestReconnect}
              />
            </TabsContent>

            <TabsContent value="field-mapping" className="mt-4">
              <FieldMappingTab onMutationPendingChange={setIsBlocking} />
            </TabsContent>

            <TabsContent value="stage-mapping" className="mt-4">
              <StageMappingTab onMutationPendingChange={setIsBlocking} />
            </TabsContent>

            <TabsContent value="adder-mapping" className="mt-4">
              <AdderMappingTab onMutationPendingChange={setIsBlocking} />
            </TabsContent>

            <TabsContent value="document-filters" className="mt-4">
              <DocumentFiltersTab
                onMutationPendingChange={setIsBlocking}
                onDirtyChange={setDirtyForTab('document-filters')}
              />
            </TabsContent>

            <TabsContent value="sync-history" className="mt-4">
              <SyncHistoryTab />
            </TabsContent>

            <TabsContent value="insights" className="mt-4">
              <InsightsTab />
            </TabsContent>

            <TabsContent value="sync-errors" className="mt-4">
              <SyncErrorsView />
            </TabsContent>

            <TabsContent value="missing-projects" className="mt-4">
              <MissingProjectsView />
            </TabsContent>
          </Tabs>

          <div className="flex items-center justify-between border-t pt-4">
            <p className="text-xs text-muted-foreground">
              Verify your configured mappings against 10 sample SCH projects before importing.
            </p>
            <div className="flex items-center gap-2">
              {/* SCH-3.5: view-last-run entry point. Only shown when no active
                  run exists (active path is covered by the Resume banner). */}
              {!activeBackfill && latestSyncRunId && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewLastRunSyncId(latestSyncRunId)}
                  className="gap-2 text-muted-foreground"
                >
                  <Eye className="h-4 w-4" />
                  View last run
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => setIsPreviewOpen(true)}
                className="gap-2"
              >
                <Eye className="h-4 w-4" />
                Run Backfill Preview
              </Button>
            </div>
          </div>
        </div>
      </SlidePanel>

      <BackfillPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => {
          setIsPreviewOpen(false);
          setResumeSyncRunId(null);
        }}
        initialSyncRunId={resumeSyncRunId ?? undefined}
      />

      {/* SCH-3.5: read-only modal for viewing a completed run. */}
      <BackfillPreviewModal
        isOpen={!!viewLastRunSyncId}
        onClose={() => setViewLastRunSyncId(null)}
        initialSyncRunId={viewLastRunSyncId ?? undefined}
        readOnly
      />

      <UnsavedChangesDialog
        isOpen={showDiscardDialog}
        onCancel={() => setShowDiscardDialog(false)}
        onDiscard={handleDiscardAndClose}
        drawerTitle="SubContractorHub configuration"
      />
    </>
  );
}

interface ResumeBannerProps {
  backfill: ActiveBackfillResponse;
  canRun: boolean;
  roleLoading: boolean;
  onResume: () => void;
}

const RESUME_ADMIN_REQUIRED_TOOLTIP =
  "Administrator access is required to resume a backfill.";

function _ResumeBanner({
  backfill,
  canRun,
  roleLoading,
  onResume,
}: ResumeBannerProps) {
  // "Resume" implies the user must click something to continue. That's only
  // accurate when the run is `paused` (orchestrator stopped, needs admin to
  // retry). For `awaiting_continue` in auto-continue mode the orchestrator
  // chains automatically, and for `processing_batch` it's actively walking
  // SCH — in both cases the button just opens the live progress view.
  const isPaused = backfill.status === 'paused';
  const buttonLabel = isPaused ? 'Resume backfill' : 'View progress';
  const ButtonIcon = isPaused ? PlayCircle : Eye;

  const descriptionText = isPaused
    ? 'The run is paused — Resume picks up from the last committed project.'
    : backfill.status === 'awaiting_continue'
      ? 'Awaiting next batch — open the progress view to see live status or trigger the next batch.'
      : 'Live import in progress — open the progress view to see batch-by-batch status.';

  const button = (
    <Button
      size="sm"
      variant="default"
      onClick={onResume}
      disabled={!canRun || roleLoading}
      className="gap-1.5"
    >
      <ButtonIcon className="h-4 w-4" />
      {buttonLabel}
    </Button>
  );

  return (
    <div className="flex items-center justify-between rounded-md border border-amber-300 bg-amber-50 p-3">
      <div className="flex flex-col gap-0.5">
        <p className="text-sm font-medium text-amber-900">
          Backfill in progress — {backfill.totalProcessed} projects imported
          {backfill.totalCatalog ? ` of ${backfill.totalCatalog}` : ''}.
        </p>
        <p className="text-xs text-amber-800">{descriptionText}</p>
      </div>
      {canRun || roleLoading ? (
        button
      ) : (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0}>
                <Button
                  size="sm"
                  variant="default"
                  disabled
                  className="cursor-not-allowed gap-1.5 opacity-60"
                >
                  <ButtonIcon className="h-4 w-4" />
                  {buttonLabel}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              {RESUME_ADMIN_REQUIRED_TOOLTIP}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}
